import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { apiClient } from "../../api/ApiClient";
import ContactDeveloper from "../dashboard/newSite/contactDeveloper/ContactDeveloper";
import JavaScriptSnippet from "../javaScriptSnippet/JavaScriptSnippet";
import FormLabel from "../login/formLabel/FormLabel";
import Button from "../mantine/button/Button";
import NotificationBlock from "../notificationBlock/NotificationBlock";
import Modal from "../ui/modal/Modal";
import Spinner from "../ui/spinner/Spinner";
import { WidgetStatus } from "../widgetStatus/WidgetStatus";
import { SiteWithAuth } from "./General";
import styles from "./SiteSettings.module.scss";

type JavaScriptSnippetAutomationProps = {
  siteData: SiteWithAuth | undefined;
  isLoading: boolean;
};

const JavaScriptSnippetAutomation = ({
  siteData,
  isLoading,
}: JavaScriptSnippetAutomationProps) => {
  const { id } = useParams();
  const site = siteData?.site;
  const queryClient = useQueryClient();
  const [openModal, setOpenModal] = useState(false);
  const [openDisableModal, setOpenDisableModal] = useState(false);
  const canUpdateWidget = siteData?.authorizations?.member
    ?.flat()
    .includes("widget_toggle");
  const { mutate: toggleWidget } = useMutation({
    mutationFn: async () => {
      await apiClient.sites.widgetToggleCreate(Number(id));
    },
    onSuccess: () => {
      enqueueSnackbar(`The widget state was successfully updated`, {
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["testWidget"] });
    },
    onError: (error) => {
      //@ts-expect-error BE types
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
  });

  const { data: widgetDetail, isLoading: isWidgetLoading } = useQuery({
    queryKey: ["siteWidget", id],
    queryFn: async () => {
      const response = await apiClient.sites.widgetDetail(Number(id));

      return response.data.site_widget;
    },
    refetchInterval: (query) => {
      if (query.state.data?.widget_installed) {
        return false;
      }

      return 15000;
    },
    enabled: !!id,
  });
  const widgetEnabled = widgetDetail?.widget_enabled;
  const widgetInstalled = widgetDetail?.widget_installed;
  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const handleOpenDisableModal = () => setOpenDisableModal(true);
  const handleCloseDisableModal = () => setOpenDisableModal(false);

  const handleDisableSnippet = () => {
    handleCloseDisableModal();
    toggleWidget();
  };

  const widgetCode = `<script type="text/javascript" src="${widgetDetail?.widget_data_uri}"></script>`;

  if (isLoading || isWidgetLoading) return <Spinner />;

  return (
    <div className={styles.jsSnippet}>
      <p className={styles.settingsSectionText}>
        Code you should include in the <strong>&lt;HEAD&gt;</strong> tag of your
        site. For best results, paste this on every page, or in the site
        template, right before the closing <strong>&lt;/HEAD&gt;</strong> tag.
        This will only affect pages and recommendations you have pre-approved on
        your{" "}
        {/*
         // @ts-expect-error TS2339 non-existing className */}
        <Link to="/recommendations" className={styles.inlineLink}>
          recommendations page
        </Link>
        .
      </p>
      <NotificationBlock
        type="info"
        message={`Note! This code will only work on ${site?.fqdn} Make sure you do not share this code across sites`}
      />

      <JavaScriptSnippet
        code={widgetCode}
        actions={
          widgetDetail && (
            <WidgetStatus
              widgetInstalled={
                widgetInstalled === undefined
                  ? !!site?.widget_installed
                  : widgetInstalled
              }
            />
          )
        }
      />

      {/*
       // @ts-expect-error TS2339 non-existing className */}
      <div className={styles.developerSection}>
        <FormLabel label="Contact Your Developer" />
        <p className={styles.settingsSectionText}>
          If you are facing any difficulties while installing JS snippet,
          consider reaching out to your developer, or we can help you with that
        </p>

        <Button
          variant="outlined"
          onClick={handleOpen}
          className={styles.jsSnippetButton}
        >
          Contact Developer
        </Button>
      </div>

      <Modal
        key="contactDeveloper"
        isOpen={openModal}
        closeModal={handleClose}
        buttonLabel="Send Email"
        formId="contactDeveloper"
        // @ts-expect-error TS2339 non-existing className
        modalLabel={<h3 className={styles.modalTitle}>Contact Developer</h3>}
      >
        <ContactDeveloper newSiteId={Number(id)} handleClose={handleClose} />
      </Modal>

      {/*
       // @ts-expect-error TS2339 non-existing className */}
      <div className={styles.snippetControlSection}>
        <FormLabel label="Work of the Snippet" />
        <p className={styles.settingsSectionText}>
          You can control work of the snippet as you disable it the code will
          become inactive immediately on all pages
        </p>
        <Button
          variant={widgetEnabled ? "danger-outline" : "outlined"}
          className={styles.jsSnippetButton}
          onClick={handleOpenDisableModal}
          disabled={!canUpdateWidget}
        >
          {widgetEnabled ? "Disable" : "Enable"} JS Snippet
        </Button>
      </div>
      <Modal
        key="disableSnippet"
        isOpen={openDisableModal}
        closeModal={handleCloseDisableModal}
        buttonLabel={widgetEnabled ? "Disable" : "Enable"}
        actionButtonColor={widgetEnabled ? "danger" : "primary"}
        modalLabel={
          // @ts-expect-error TS2339 non-existing className
          <h3 className={styles.modalTitle}>
            {widgetEnabled ? "Disable JS Snippet" : "Enable JS Snippet"}
          </h3>
        }
        action={handleDisableSnippet}
      >
        {/*
         // @ts-expect-error TS2339 non-existing className */}
        <div className={styles.disableSnippetModalContent}>
          {/*
           // @ts-expect-error TS2339 non-existing className */}
          <p className={styles.disableWarningText}>
            {widgetEnabled
              ? "You are going to disable JS Snippet it will become inactive immediately on all of your pages, and your site will no longer reflect our suggestions. Suggestion will still be available for approval, but won`t be implemented. As soon as you enable the snippet approved recommendations will be automatically implemented"
              : "You can re-enable the Snippet by clicking the button below. The code above will become active immediately, so please take a moment to review the recommendations that will be active on your site."}
          </p>
          {widgetEnabled && (
            <NotificationBlock
              type="info"
              message="Note! If you are not going to use JS snippet anymore, to avoid errors, please do not forget to uninstall the snippet from your website"
            />
          )}
          <div className={styles.jsSnippetSpace} />
        </div>
      </Modal>
    </div>
  );
};

export default JavaScriptSnippetAutomation;
